import { useMemo } from 'react';

import { usePublicCategoryFilter } from '@/module/public/hooks/usePublicCategoryFilter';
import { usePublicMetaFilter } from '@/module/public/hooks/usePublicMetaFilter';
import { usePublicSearchFilterQuery } from '@/module/public/hooks/usePublicSearchFilterQuery';
import {
  buildActiveSearchQuizsetFilterCriteria,
  buildBrowseQuizsetFilterCriteria,
  buildInitialSearchQuizsetFilterCriteria,
  buildSubjectFilter,
  buildSubjectFilterFromSearchSubject,
} from '@/module/public/util/publicQuizsetSearchPayload';
import { useCountryCode } from '@/shared/hooks';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';

export const useGetAllQuizesPayload = () => {
  const { countryCode, isLoading } = useCountryCode();

  const {
    selectedSubject,
    isSubSubjectSelected,
    selectedSemesters,
    selectedSchoolTypeName,
    selectedGrades,
    initialized,
  } = usePublicCategoryFilter();

  const { searchText, searchType, order, period } = useSearchQuizQuery();

  const {
    selectedQuestionSize,
    selectedPublishers,
    selectedMapTemplate,
    checkedOfficialQuiz,
    autoApply,
  } = usePublicMetaFilter();

  const subjectFilter = useMemo(
    () => buildSubjectFilter({ selectedSubject, isSubSubjectSelected }),
    [selectedSubject, isSubSubjectSelected],
  );

  const categoryFilters = useMemo(
    () =>
      buildBrowseQuizsetFilterCriteria({
        searchText,
        searchType,
        countryCode,
        checkedOfficialQuiz,
        selectedSchoolTypeName,
        selectedGrades,
        selectedSemesters,
        selectedSubject,
        isSubSubjectSelected,
        selectedPublishers,
        selectedMapTemplate,
        selectedQuestionSize,
      }),
    [
      searchText,
      searchType,
      countryCode,
      checkedOfficialQuiz,
      selectedSchoolTypeName,
      selectedGrades,
      selectedSemesters,
      selectedSubject,
      isSubSubjectSelected,
      selectedPublishers,
      selectedMapTemplate,
      selectedQuestionSize,
    ],
  );

  const enabled = initialized && autoApply && !isLoading;

  return {
    categoryFilters,
    subjectFilter,
    enabled,
    order: order,
    period: period,
  };
};

export const usePublicQuizListPayload = () => {
  const browsePayload = useGetAllQuizesPayload();
  const { countryCode } = useCountryCode();
  const { searchText, searchType } = useSearchQuizQuery();
  const { state: searchFilterState, hasActiveSearchFilters } =
    usePublicSearchFilterQuery();
  const { selectedSchoolTypeName } = usePublicCategoryFilter();

  const categoryFilters = useMemo(() => {
    if (!searchText && !hasActiveSearchFilters) {
      return browsePayload.categoryFilters;
    }

    if (!hasActiveSearchFilters) {
      return buildInitialSearchQuizsetFilterCriteria({
        searchText,
        searchType,
        countryCode,
        selectedSchoolTypeName,
      });
    }

    return buildActiveSearchQuizsetFilterCriteria({
      searchText,
      searchType,
      countryCode,
      selectedSchoolTypeName,
      selectedGrades: searchFilterState.grades,
      selectedSemesters: searchFilterState.semesters,
      selectedSubject: searchFilterState.subject,
      selectedPublishers: searchFilterState.publishers,
      checkedOfficialQuiz: searchFilterState.official,
      selectedMapTemplate: searchFilterState.templateType,
      selectedQuestionSize: searchFilterState.quizCountRange,
    });
  }, [
    browsePayload.categoryFilters,
    countryCode,
    hasActiveSearchFilters,
    searchFilterState,
    searchText,
    searchType,
    selectedSchoolTypeName,
  ]);

  const subjectFilter = useMemo(
    () =>
      hasActiveSearchFilters
        ? buildSubjectFilterFromSearchSubject(searchFilterState.subject)
        : !searchText
        ? browsePayload.subjectFilter
        : {},
    [
      browsePayload.subjectFilter,
      hasActiveSearchFilters,
      searchFilterState.subject,
      searchText,
    ],
  );

  return {
    ...browsePayload,
    categoryFilters,
    subjectFilter,
  };
};

export type GetAllQuizesPayload = ReturnType<typeof useGetAllQuizesPayload>;
